import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Grid
} from '@mui/material';
import { Add, Edit, Delete, Home, Receipt, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';

const API = process.env.REACT_APP_API_URL;
const emptyForm = { code: '', name: '', vatPercent: '', vatSalesAccount: '', vatPurchaseAccount: '' };

const VATPostingGroupList = () => {
  const navigate = useNavigate();
  const { accounts: glAccounts } = useGLAccounts();
  const [groups,     setGroups]     = useState([]);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/vat-posting-groups`, { headers });
      setGroups(data);
    } catch { setError('Failed to load VAT posting groups'); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);


  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setError(''); setDialogOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ code: g.code, name: g.name, vatPercent: g.vatPercent,
              vatSalesAccount: g.vatSalesAccount || '', vatPurchaseAccount: g.vatPurchaseAccount || '' });
    setError(''); setDialogOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Code is required'); return; }
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name,
        vatPercent: parseFloat(form.vatPercent) || 0,
        vatSalesAccount: form.vatSalesAccount,
        vatPurchaseAccount: form.vatPurchaseAccount
      };
      if (editing) {
        await axios.put(`${API}/api/academics/vat-posting-groups/${editing.code}`, payload, { headers });
        setSuccess('Updated');
      } else {
        await axios.post(`${API}/api/academics/vat-posting-groups`,
          { code: form.code.toUpperCase(), ...payload }, { headers });
        setSuccess('Created');
      }
      setDialogOpen(false); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = (g) => {
    setConfirmDlg(g);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(`${API}/api/academics/vat-posting-groups/${confirmDlg.code}`, { headers });
      setSuccess('Deleted');
      setConfirmDlg(null);
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setConfirmDlg(null);
    }
  };

  const glLabel = (no) => {
    if (!no) return '—';
    const a = glAccounts.find(x => x.accountNo === no);
    return a ? `${no} — ${a.accountName}` : no;
  };

  const rateColor = (pct) => {
    if (pct === 0) return 'default';
    if (pct <= 5)  return 'info';
    return 'warning';
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Receipt color="secondary" />
              <Typography variant="h5" color="secondary">VAT Posting Groups</Typography>
              <Chip label={groups.length} size="small" color="secondary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Setups">
                <IconButton onClick={() => navigate('/setups')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" color="secondary" startIcon={<Add />} onClick={openCreate}>Add Group</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Defines VAT rates and the GL accounts for output/input VAT. Linked to charge items when isVatable is enabled.
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell align="center"><strong>VAT %</strong></TableCell>
                <TableCell><strong>Sales Account</strong></TableCell>
                <TableCell><strong>Purchase Account</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No VAT groups. Run <code>node backend/scripts/seed-posting-groups.js</code> or add manually.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : groups.map(g => (
                <TableRow key={g.code} hover>
                  <TableCell><strong>{g.code}</strong></TableCell>
                  <TableCell>{g.name}</TableCell>
                  <TableCell align="center">
                    <Chip label={`${g.vatPercent}%`} size="small" color={rateColor(g.vatPercent)} />
                  </TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{glLabel(g.vatSalesAccount)}</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{glLabel(g.vatPurchaseAccount)}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(g)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(g)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete VAT Posting Group <strong>{confirmDlg?.code} — {confirmDlg?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'Add VAT Posting Group'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Code *" name="code" value={form.code} onChange={handle}
                fullWidth size="small" disabled={!!editing}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText="e.g. STANDARD" />
            </Grid>
            <Grid item xs={5}>
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} fullWidth size="small" helperText="e.g. Standard Rate" />
            </Grid>
            <Grid item xs={3}>
              <TextField label="VAT %" name="vatPercent" type="number" value={form.vatPercent}
                onChange={handle} fullWidth size="small"
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                helperText="e.g. 16" />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Sales Account (Output VAT)" name="vatSalesAccount"
                value={form.vatSalesAccount} onChange={handle} fullWidth size="small">
                {glAccountOptions(glAccounts, { noneLabel: '— Not set —' })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Purchase Account (Input VAT)" name="vatPurchaseAccount"
                value={form.vatPurchaseAccount} onChange={handle} fullWidth size="small">
                {glAccountOptions(glAccounts, { noneLabel: '— Not set —' })}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VATPostingGroupList;
